import { useEffect, useRef, useState } from 'react';
import { Node, mergeAttributes } from '@tiptap/core';
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import type { NodeViewProps } from '@tiptap/react';

import { t } from '../i18n';
import { Glyph } from './icons';
import { MediaLightbox } from './MediaLightbox';
import { PdfPage, usePdf } from './PdfPage';

export type MediaKind = 'image' | 'video' | 'audio' | 'pdf' | 'file';
export type MediaMode = 'link' | 'card' | 'embed';

/** The kinds of file the block can show. */
export const MEDIA_KINDS: MediaKind[] = ['image', 'video', 'audio', 'pdf', 'file'];

/** The address of a file, served by our own API so it stays private. */
export function mediaUrl(fileId: string): string {
  return `/api/files/${fileId}/content`;
}

/** A human size: 1,2 MB. */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} kB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
}

const MIN_WIDTH = 20;
const MAX_WIDTH = 100;
/** The widths a drag clings to, as a percentage of the page column. */
const SNAP_WIDTHS = [25, 33, 50, 66, 75, 100];
const SNAP_DISTANCE = 2.5;
const KEY_STEP = 5;
const DEFAULT_WIDTH: Record<MediaKind, number> = {
  image: 60,
  video: 60,
  audio: 100,
  pdf: 70,
  file: 60,
};
/** A first page is shown at a fixed pixel size and scaled down by CSS. */
const PREVIEW_PAGE_PX = 640;

/** Round to the nearest snap width when close enough, else to a whole percent. */
export function snapWidth(percent: number): number {
  const clamped = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, percent));
  const near = SNAP_WIDTHS.find((width) => Math.abs(clamped - width) < SNAP_DISTANCE);
  return near ?? Math.round(clamped);
}

/** A file is a card until the writer asks for a preview; the rest preview. */
export function defaultMode(kind: MediaKind): MediaMode {
  return kind === 'file' ? 'card' : 'embed';
}

/** Only pictures, videos and PDFs have a size worth changing. */
export function isResizable(kind: MediaKind, mode: MediaMode): boolean {
  return mode === 'embed' && (kind === 'image' || kind === 'video' || kind === 'pdf');
}

/** "JPG · 3,2 MB", with the page count of a PDF when it is known. */
export function mediaMeta(filename: string, size: number, pages: number | null): string {
  const dot = filename.lastIndexOf('.');
  const parts = [dot > 0 ? filename.slice(dot + 1).toUpperCase() : ''];
  if (size > 0) parts.push(formatFileSize(size));
  if (pages) parts.push(t('media.pages', { count: String(pages) }));
  return parts.filter(Boolean).join(' · ');
}

const KIND_ICON: Record<MediaKind, string> = {
  image: 'M3 5h18v14H3z M3 16l5-5 4 4 3-3 6 6 M8.5 9.5h.01',
  video: 'M3 6h13v12H3z M16 10l5-3v10l-5-3',
  audio: 'M9 18V6l10-2v12 M9 18a3 3 0 1 1-3-3 M19 16a3 3 0 1 1-3-3',
  pdf: 'M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z M14 3v5h5',
  file: 'M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z M14 3v5h5',
};
const TOOL_ICON = {
  link: 'M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.7 1.7 M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.7-1.7',
  card: 'M3 7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z M7 10h7 M7 14h4',
  preview: 'M3 4h18v16H3z M3 15l5-5 4 4 3-3 6 6',
  download: 'M12 4v12 M7 11l5 5 5-5 M5 20h14',
  fullscreen: 'M4 9V4h5 M20 9V4h-5 M4 15v5h5 M20 15v5h-5',
  remove: 'M4 7h16 M9 7V4h6v3 M6 7l1 13h10l1-13',
};

/** Lets the empty block ask the editor to open its file picker. */
export interface MediaOptions {
  picker: { current: ((kind: MediaKind, pos: number) => void) | null };
}

/**
 * A file in the page: an image, a video, audio, a PDF or any other document.
 * The block keeps the id of the file; the bytes live in the bucket and come
 * through the API, so the file is as private as the page. It shows as a link,
 * a card or a full preview, and a preview can be dragged to another width.
 */
export const Media = Node.create<MediaOptions>({
  name: 'media',
  group: 'block',
  atom: true,
  draggable: true,

  addOptions() {
    return { picker: { current: null } };
  },

  addAttributes() {
    return {
      fileId: { default: null },
      kind: { default: 'file' },
      filename: { default: '' },
      contentType: { default: '' },
      size: { default: 0 },
      mode: { default: null },
      w: { default: null },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-media-file]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-media-file': '' })];
  },

  addNodeView() {
    return ReactNodeViewRenderer(MediaView);
  },
});

function MediaView({
  node,
  extension,
  getPos,
  updateAttributes,
}: NodeViewProps): React.ReactElement {
  const fileId = typeof node.attrs['fileId'] === 'string' ? node.attrs['fileId'] : '';
  const kind = (node.attrs['kind'] as MediaKind) ?? 'file';
  const filename = typeof node.attrs['filename'] === 'string' ? node.attrs['filename'] : '';
  const size = typeof node.attrs['size'] === 'number' ? node.attrs['size'] : 0;
  const storedMode = node.attrs['mode'] as MediaMode | null;
  const storedWidth = typeof node.attrs['w'] === 'number' ? node.attrs['w'] : null;

  const [viewing, setViewing] = useState(false);
  const [liveWidth, setLiveWidth] = useState<number | null>(null);
  const [pages, setPages] = useState<number | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  if (!fileId) {
    return (
      <NodeViewWrapper className="media" data-media-file="">
        <div contentEditable={false} className="media__empty">
          <span className="media__emptyicon" aria-hidden="true">
            <Glyph d={KIND_ICON[kind]} size={20} />
          </span>
          <span className="media__emptytitle">{t('media.add', { kind: t(`media.${kind}`) })}</span>
          <button
            type="button"
            className="media__choose"
            onClick={() => {
              const pos = getPos();
              if (pos !== undefined)
                (extension.options as MediaOptions).picker.current?.(kind, pos);
            }}
          >
            {t('media.choose')}
          </button>
        </div>
      </NodeViewWrapper>
    );
  }

  const source = mediaUrl(fileId);
  const label = filename || t('media.untitled');
  const mode = storedMode ?? defaultMode(kind);
  const width = liveWidth ?? storedWidth ?? DEFAULT_WIDTH[kind];
  const resizable = isResizable(kind, mode);
  const meta = mediaMeta(filename, size, pages);
  const open = (): void => setViewing(true);

  const startResize = (event: React.PointerEvent): void => {
    event.preventDefault();
    event.stopPropagation();
    const wrap = wrapRef.current;
    const column = wrap?.parentElement;
    if (!wrap || !column) return;
    const left = wrap.getBoundingClientRect().left;
    const full = column.getBoundingClientRect().width || 1;
    let latest = width;
    setLiveWidth(width);
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'ew-resize';
    const move = (moved: PointerEvent): void => {
      latest = snapWidth(((moved.clientX - left) / full) * 100);
      setLiveWidth(latest);
    };
    const finish = (): void => {
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', finish);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      // One transaction per drag, so one undo step undoes the whole resize.
      updateAttributes({ w: latest });
      setLiveWidth(null);
    };
    document.addEventListener('pointermove', move);
    document.addEventListener('pointerup', finish);
  };

  const resizeByKey = (event: React.KeyboardEvent): void => {
    const step = event.key === 'ArrowRight' ? KEY_STEP : event.key === 'ArrowLeft' ? -KEY_STEP : 0;
    if (step === 0) return;
    event.preventDefault();
    updateAttributes({ w: snapWidth(width + step) });
  };

  const wrapStyle: React.CSSProperties =
    mode === 'link'
      ? { display: 'inline-flex', maxWidth: '100%' }
      : mode === 'card'
        ? { width: '100%' }
        : { width: `${width}%` };

  const tools = [
    { mode: 'link', label: t('media.showLink'), icon: TOOL_ICON.link },
    { mode: 'card', label: t('media.showCard'), icon: TOOL_ICON.card },
    { mode: 'embed', label: t('media.showPreview'), icon: TOOL_ICON.preview },
  ] as const;

  return (
    <NodeViewWrapper className="media" data-media-file="" data-media-kind={kind}>
      <div
        ref={wrapRef}
        contentEditable={false}
        className={liveWidth === null ? 'media__wrap' : 'media__wrap media__wrap--active'}
        style={wrapStyle}
      >
        {mode === 'link' ? (
          <button type="button" className="media__link" title={t('media.openFile')} onClick={open}>
            <Glyph d={KIND_ICON[kind]} />
            <span className="media__linkname">{label}</span>
            <span className="media__linkmeta">{meta}</span>
          </button>
        ) : null}

        {mode === 'card' ? (
          <div className="media__card" title={t('media.dblclick')} onDoubleClick={open}>
            <span className="media__cardicon" aria-hidden="true">
              <Glyph d={KIND_ICON[kind]} size={20} />
            </span>
            <span className="media__cardtext">
              <span className="media__cardname">{label}</span>
              <span className="media__cardsize">{meta}</span>
            </span>
            <button type="button" className="media__open" onClick={open}>
              {t('media.open')}
            </button>
          </div>
        ) : null}

        {mode === 'embed' ? (
          <div className="media__embed" title={t('media.dblclick')} onDoubleClick={open}>
            {kind === 'image' ? (
              <img className="media__image" src={source} alt={label} loading="lazy" />
            ) : null}
            {kind === 'video' ? (
              <video className="media__video" src={source} controls preload="metadata" />
            ) : null}
            {kind === 'audio' ? (
              <div className="media__audiorow">
                <audio className="media__audio" src={source} controls preload="metadata" />
              </div>
            ) : null}
            {kind === 'pdf' ? (
              <PdfPreview source={source} label={label} onPages={setPages} />
            ) : null}
            {kind === 'file' ? (
              <div className="media__filerow">
                <Glyph d={KIND_ICON.file} size={20} />
              </div>
            ) : null}
            <div className="media__foot">
              <span className="media__footname">{label}</span>
              <span>{meta}</span>
            </div>
          </div>
        ) : null}

        <div
          className="media__toolbar"
          role="toolbar"
          aria-label={t('media.options')}
          data-placement={mode === 'embed' ? 'inside' : 'above'}
        >
          {tools.map((tool) => (
            <button
              key={tool.mode}
              type="button"
              className="media__tool"
              aria-label={tool.label}
              aria-pressed={mode === tool.mode}
              title={tool.label}
              onClick={() => updateAttributes({ mode: tool.mode })}
            >
              <Glyph d={tool.icon} />
            </button>
          ))}
          <span className="media__toolsep" aria-hidden="true" />
          <a
            className="media__tool"
            href={source}
            download={label}
            aria-label={t('media.download')}
            title={t('media.download')}
          >
            <Glyph d={TOOL_ICON.download} />
          </a>
          <button
            type="button"
            className="media__tool"
            aria-label={t('media.fullscreen')}
            title={t('media.fullscreen')}
            onClick={open}
          >
            <Glyph d={TOOL_ICON.fullscreen} />
          </button>
          <span className="media__toolsep" aria-hidden="true" />
          <button
            type="button"
            className="media__tool"
            aria-label={t('media.remove')}
            title={t('media.remove')}
            onClick={() => updateAttributes({ fileId: null })}
          >
            <Glyph d={TOOL_ICON.remove} />
          </button>
        </div>

        {resizable ? (
          <div
            className="media__handle"
            role="separator"
            aria-orientation="vertical"
            aria-label={t('media.resize', { width: `${Math.round(width)}%` })}
            aria-valuemin={MIN_WIDTH}
            aria-valuemax={MAX_WIDTH}
            aria-valuenow={Math.round(width)}
            tabIndex={0}
            title={t('media.resize', { width: `${Math.round(width)}%` })}
            onPointerDown={startResize}
            onKeyDown={resizeByKey}
          >
            <i />
          </div>
        ) : null}
      </div>

      {viewing ? (
        <MediaLightbox
          kind={kind}
          source={source}
          filename={label}
          meta={meta}
          onClose={() => setViewing(false)}
        />
      ) : null}
    </NodeViewWrapper>
  );
}

/** The first page of a PDF, drawn by pdf.js, with the page count beneath it. */
function PdfPreview({
  source,
  label,
  onPages,
}: {
  source: string;
  label: string;
  onPages: (pages: number) => void;
}): React.ReactElement {
  const pdf = usePdf(source);
  const pageCount = pdf.status === 'ready' ? pdf.document.numPages : 0;
  useEffect(() => {
    if (pageCount > 0) onPages(pageCount);
  }, [pageCount, onPages]);
  return (
    <div className="media__pdf">
      {pdf.status === 'ready' ? (
        <>
          <PdfPage
            document={pdf.document}
            pageNumber={1}
            width={PREVIEW_PAGE_PX}
            fluid
            label={label}
            className="media__pdfpage"
          />
          <span className="media__pdfbadge">
            {t('media.pageOf', { page: '1', pages: String(pdf.document.numPages) })}
          </span>
        </>
      ) : null}
      {pdf.status === 'failed' ? (
        <span className="media__pdfnote">{t('media.pdfFailed')}</span>
      ) : null}
    </div>
  );
}
